/**
 * cellEditorSession.ts - The floating cell editor's lifecycle: committing an edit, positioning the
 * editor over the cell being edited, and bringing that cell back into view. Private to
 * `createTableInteractions`.
 */

import {assert} from 'Asserts';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import type {TableDom} from 'notebook/tables/store/logic/interactions/tableDom';
import {DRAG_SCROLL_DURATION, TableScroller} from 'notebook/tables/store/logic/interactions/tableScroller';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

export interface CellEditorSession {
  /** Commits the in-progress cell edit, if there is one. Runs even while the table is unmounting. */
  finalizeEdit: () => void;
  /**
   * Positions the cell editor over the cell at `index`. When the cell is not rendered or is out of
   * view, scrolls it into view instead and retries once the scroll settles, while the edit is still
   * active and the editor has not been positioned.
   */
  positionEditor: (index: TableIndex) => void;
  /**
   * Clears the cell editor's position after an edit, first scrolling back to the edited cell when the
   * user scrolled away from it during the edit.
   */
  finishEditorAfterEdit: () => void;
  /** Scrolls the table, then the cell at `index`, back into view. */
  revealEditingCell: (index: TableIndex) => void;
}

export function createCellEditorSession({
  dom,
  scroller,
  getTableModel,
  getTableProps,
  setState,
  isMounted,
  onScrollSettled,
}: {
  dom: TableDom;
  scroller: TableScroller;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
  isMounted: () => boolean;
  /** Called whenever a scroll this session started settles. */
  onScrollSettled: () => void;
}): CellEditorSession {
  const {elements} = dom;

  const isShowingCellEditor = (): boolean => {
    const {tableNamespace, table, fileId} = getTableProps();
    return SpreadsheetStore.getState(tableNamespace, table.name, fileId).cellEditorPosition != null;
  };

  const scrollCellIntoView = (index: TableIndex): void => {
    scroller.scrollCellIntoView(index, onScrollSettled);
  };

  const finalizeEdit = (): void => {
    const {multiEditing, tableNamespace, table, fileId} = getTableProps();
    const {state, mutations} = getTableModel();
    if (!(multiEditing && state.multiSelIndex != null)) {
      return;
    }
    const {formula, typedValue} = SpreadsheetStore.getState(tableNamespace, table.name, fileId);
    assert(typedValue !== undefined, 'typedValue can be null but not undefined');
    mutations.finalizeMultiEdit(state.multiSelIndex, formula, typedValue, {
      beforePersist: () => {
        setCellEditorPosition(null);
      },
    });
  };

  /** Retries positioning the editor once a scroll settles, if the edit is still waiting for it. */
  const retryPositioningAfter = (delay: number): void => {
    scroller.schedule(() => {
      const {multiSelIndex} = getTableModel().state;
      if (getTableProps().multiEditing && multiSelIndex && !isShowingCellEditor()) {
        positionEditor(multiSelIndex);
      }
    }, delay);
  };

  const positionEditor = (index: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const [rowIndex, columnIndex] = index;
    const activeCell = dom.findCellElement(index);
    if (!activeCell) {
      scroller.scrollTableIntoView();
      scroller.schedule(() => {
        scrollCellIntoView(index);
      }, DRAG_SCROLL_DURATION);
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: true});
      retryPositioningAfter(DRAG_SCROLL_DURATION * 2); // Table scroll + cell scroll
      return;
    }
    const tableScrollContainerRect =
      dom.getTableVerticalScrollContainer(getTableModel().views)?.getBoundingClientRect() ?? null;
    const activeCellRect = activeCell.getBoundingClientRect();
    const pageScrollContainerRect = elements.pageVerticalScrollContainer?.getBoundingClientRect() ?? null;
    const stickyHeaderRect = elements.stickyHeader?.getBoundingClientRect() ?? null;
    const {state, singleInputs, visibility} = getTableModel();
    const headerRowHeight = state.rowHeights[0] ?? 0;
    // This may be a ghost cell, so we're not guaranteed to have a computed value yet
    const {computedTable} = getTableProps();
    const activeCellIsMerged = computedTable[rowIndex]?.[columnIndex]?.mergedBox !== undefined;
    assert(
      tableScrollContainerRect != null && pageScrollContainerRect != null,
      'Expected to find the horizontal and vertical scroll containers when starting an edit'
    );
    const topIsOutOfView = pageScrollContainerRect.top > activeCellRect.top;
    const bottomIsOutOfView = pageScrollContainerRect.bottom < activeCellRect.bottom;
    const leftIsOutOfView = tableScrollContainerRect.left > activeCellRect.left;
    const rightIsOutOfView = tableScrollContainerRect.right < activeCellRect.right;
    const activeCellIsTallerThanView = activeCellRect.height > pageScrollContainerRect.height;
    if (
      !state.tableCellEditorIsLoading &&
      !activeCellIsMerged &&
      // If the cell is taller than the view, we really only care about the top of the cell being in view
      // so don't scroll if the top is already visible
      !(activeCellIsTallerThanView && !topIsOutOfView) &&
      (topIsOutOfView || bottomIsOutOfView || leftIsOutOfView || rightIsOutOfView)
    ) {
      scrollCellIntoView(index);
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: true});
      retryPositioningAfter(DRAG_SCROLL_DURATION);
      return;
    }
    setCellEditorPosition(
      TableUtil.getTableCellEditorPosition(
        tableScrollContainerRect,
        activeCellRect,
        headerRowHeight,
        columnIndex < (singleInputs.boundaryIndex ?? 0),
        visibility.isStickyHeaderVisible,
        stickyHeaderRect
      )
    );
    setState({hasScrolledSinceEdit: false, tableCellEditorIsLoading: false});
  };

  const finishEditorAfterEdit = (): void => {
    if (!isShowingCellEditor()) {
      return;
    }
    const {hasScrolledSinceEdit, multiSelIndex} = getTableModel().state;
    if (hasScrolledSinceEdit && multiSelIndex) {
      scrollCellIntoView(multiSelIndex);
      scroller.scrollTableIntoView();
      setCellEditorPosition(null);
      setState({hasScrolledSinceEdit: false});
    } else {
      // If they haven't scrolled, then just wipe the table cell editor position since we're done editing
      setCellEditorPosition(null);
    }
  };

  return {
    finalizeEdit,
    positionEditor,
    finishEditorAfterEdit,
    revealEditingCell: (index) => {
      scroller.scrollTableIntoView();
      scrollCellIntoView(index);
    },
  };
}
